import { format, isValid, parse, parseISO } from 'date-fns'
import isEqual from 'lodash/isEqual'
import { type ICalculation, type KeyStatSurveyItem, type ReportOption, type ReportsV2, type SurveyResponseFilter } from '../../../models/Report'
import { type SurveyCompletionRate } from '../../../models/Division'
import { notification } from '../../../components/Notifications'
import { HttpStatusCode } from '../../../consts/httpStatusCode'
import { formatErrorMessage } from '../../../helpers/errorResponseGenerate'
import { SurveyQuestionType } from '../../../models/Survey'
import { isInstructorGradePostingReleaseMessage } from '../../../consts/messages'
import { type ComparisonOptionMetadata, type DisplayResponseFilterSetting, type DisplaySettings, type KeyStatKey, type ReportChartType, type VisibleKeyStats } from '../types'
import { allCalculationKeys, chartSettingsSwitches, chartTypeDefaultVisibleKeyStatNames, defaultVisibleKeyStatNames, keyStatKeyToCalculationName, keyStatSwitches } from './constants'

export const DISPLAY_SETTINGS_STORAGE_KEY = 'displaySettings'

export interface ReportResponseFilterQuestionOption {
  itemId: string
  revision: number
  prompt: string
  sectionTitle: string
  questionNumberLabel: string
  answers: string[]
}

interface ActiveDisplayResponseFilterSetting extends DisplayResponseFilterSetting {
  itemId: string
  revision: number
  prompt: string
  answers: string[]
}

interface NonMultiReportOption {
  name: string
  count?: number | null
  percent?: number | null
  weight?: number | null
  counts?: number[]
  percents?: number[]
  weights?: Array<number | null>
}

type NormalizedNonMultiReportOption = ReportOption & {
  count?: number | null
  percent?: number | null
  weight?: number | null
}

export interface InstructorCompareTarget {
  target_type: 'object' | 'user'
  object_id: string
  scope_object_id?: string
}

/**
 * `${attributeKey}|${attributeValue}` — the shared identity for matching a
 * slice item against the active role's attribute scope. Same key format used
 * in SlicesSection so a single Set works for both the dropdown lock and the
 * Compare-payload filter.
 */
export const scopedSliceKey = (title: string, value: string): string => `${title}|${value}`

export const formatReportPayload = (
  data: DisplaySettings,
  scopedKeys?: Set<string>
): { calculations: string[], compareTargets: InstructorCompareTarget[], sliceIds: any[] } => {
  // Locked (role-scoped) attributes are server-applied — the BE already filters
  // report data by the Viewer's role scope, so re-sending them as slice_ids
  // would double-scope and miss legitimate non-scope slices the user picked.
  const slices = data.slices ?? []
  const visibleSlices = scopedKeys != null && scopedKeys.size > 0
    ? slices.filter(s => !scopedKeys.has(scopedSliceKey(s.attributeKey ?? '', s.attributeValue ?? '')))
    : slices
  const sliceIds = visibleSlices.map((item: any) => item.attributeValueId)
  const compareTargets: InstructorCompareTarget[] = (data.comparisons || [])
    .filter(c => c.comparison)
    .map(c => c.compareBy === 'instructor'
      ? { target_type: 'user' as const, object_id: c.userId ?? c.comparison ?? '' }
      : { target_type: 'object' as const, object_id: c.comparison ?? '' }
    )
  return {
    calculations: [...allCalculationKeys],
    compareTargets,
    sliceIds
  }
}

export const normalizeResponseFilter = (value: unknown): ActiveDisplayResponseFilterSetting | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const candidate = value as Partial<DisplayResponseFilterSetting>
  if (!candidate.itemId || candidate.revision == null || !candidate.prompt) {
    return null
  }

  const answers = Array.isArray(candidate.answers)
    ? candidate.answers
      .map((answer: unknown) => typeof answer === 'string' ? answer.trim() : '')
      .filter((answer): answer is string => Boolean(answer))
    : []

  if (answers.length === 0) {
    return null
  }

  return {
    itemId: String(candidate.itemId),
    revision: Number(candidate.revision),
    prompt: String(candidate.prompt),
    answers,
    sectionTitle: candidate.sectionTitle ? String(candidate.sectionTitle) : null,
    questionNumberLabel: candidate.questionNumberLabel ? String(candidate.questionNumberLabel) : null
  }
}

export const hasActiveResponseFilter = (settings?: Partial<DisplaySettings> | null): boolean =>
  Boolean(normalizeResponseFilter(settings?.responseFilter))

export const shouldConfirmResponseFilterSwitch = (
  settings: Pick<DisplaySettings, 'responseFilter'> | null | undefined,
  nextItemId: string
): boolean => {
  const activeResponseFilter = normalizeResponseFilter(settings?.responseFilter)

  if (!activeResponseFilter) {
    return false
  }

  return activeResponseFilter.itemId !== nextItemId
}

export const formatResponseFilterPayload = (data: DisplaySettings): SurveyResponseFilter | null => {
  const responseFilter = normalizeResponseFilter(data.responseFilter)

  if (!responseFilter) {
    return null
  }

  return {
    itemId: responseFilter.itemId,
    revision: responseFilter.revision,
    targetAnswers: responseFilter.answers,
    calculations: [...allCalculationKeys]
  }
}

export const normalizeNonMultiReportOption = (option: NonMultiReportOption): NormalizedNonMultiReportOption => {
  if (Array.isArray(option.counts) || Array.isArray(option.percents) || Array.isArray(option.weights)) {
    return {
      ...option,
      counts: option.counts ?? [typeof option.count === 'number' ? option.count : 0],
      percents: option.percents ?? [typeof option.percent === 'number' ? option.percent : 0],
      ...(option.weights
        ? { weights: option.weights }
        : option.weight != null
          ? { weights: [option.weight] }
          : {})
    }
  }

  return {
    ...option,
    counts: [typeof option.count === 'number' ? option.count : 0],
    percents: [typeof option.percent === 'number' ? option.percent : 0],
    ...(option.weight != null ? { weights: [option.weight] } : {})
  }
}

export const normalizeNonMultiReportCalculations = (
  calculations: Array<ICalculation | Record<string, any>> | null | undefined
): ICalculation[] =>
  (calculations ?? []).map((calculation) => {
    const rawCalculation = calculation as ICalculation & { value?: unknown }

    return {
      ...calculation,
      values: Array.isArray(rawCalculation.values)
        ? rawCalculation.values
        : Array.isArray(rawCalculation.value)
          ? rawCalculation.value
          : rawCalculation.values == null && rawCalculation.value == null
            ? []
            : [rawCalculation.values ?? rawCalculation.value]
    }
  }) as ICalculation[]

export const normalizeNonMultiSurveyItem = (
  surveyItem?: KeyStatSurveyItem | Record<string, any> | null
): KeyStatSurveyItem | null => {
  if (!surveyItem) {
    return null
  }

  if (
    typeof surveyItem.itemId !== 'string' ||
    !Object.values(SurveyQuestionType).includes(surveyItem.itemType as SurveyQuestionType) ||
    typeof surveyItem.prompt !== 'string' ||
    typeof surveyItem.revision !== 'number'
  ) {
    return null
  }

  const normalizedOptions: ReportOption[] = Array.isArray(surveyItem.options)
    ? surveyItem.options.map((option: NonMultiReportOption): ReportOption => normalizeNonMultiReportOption(option))
    : []

  const normalizedSurveyItem: KeyStatSurveyItem = {
    itemId: surveyItem.itemId,
    itemType: surveyItem.itemType,
    prompt: surveyItem.prompt,
    revision: surveyItem.revision,
    options: normalizedOptions,
    calculations: normalizeNonMultiReportCalculations(surveyItem.calculations)
  }

  return normalizedSurveyItem
}

export const normalizeNonMultiReport = (
  report?: ReportsV2 | Record<string, any> | null,
  fallbackTitle = 'Current Term'
): ReportsV2 => {
  const safeReport = report ?? {}
  const rawTitles = Array.isArray((safeReport as ReportsV2).titles) ? (safeReport as ReportsV2).titles : []

  return {
    calculations: normalizeNonMultiReportCalculations((safeReport as ReportsV2).calculations),
    surveyScores: Array.isArray((safeReport as ReportsV2).surveyScores) ? (safeReport as ReportsV2).surveyScores : [],
    titles: rawTitles.length > 0 ? rawTitles : [fallbackTitle],
    surveySections: Array.isArray((safeReport as ReportsV2).surveySections)
      ? (safeReport as ReportsV2).surveySections.map((section: any) => ({
          ...section,
          surveyItems: Array.isArray(section?.surveyItems)
            ? section.surveyItems.map((surveyItem: Record<string, any>) => normalizeNonMultiSurveyItem(surveyItem))
            : normalizeNonMultiSurveyItem(section?.surveyItems)
        }))
      : []
  }
}

interface CommentsExportQuestionScope {
  surveyItemId?: string
  revisionId?: number
}

export const buildCommentsExportPayload = (
  data: Pick<DisplaySettings, 'slices'>,
  questionScope?: CommentsExportQuestionScope
): {
  slice_ids?: string[]
  survey_item_id?: string
  revision_id?: number
} => {
  const normalizedSliceIds = (data.slices || [])
    .map((item: any) => item?.attributeValueId)
    .filter((value: unknown): value is string => typeof value === 'string' && value.trim().length > 0)

  const payload: {
    slice_ids?: string[]
    survey_item_id?: string
    revision_id?: number
  } = {}

  if (normalizedSliceIds.length > 0) {
    payload.slice_ids = Array.from(new Set(normalizedSliceIds)).sort()
  }

  if (questionScope?.surveyItemId && questionScope?.revisionId != null) {
    payload.survey_item_id = questionScope.surveyItemId
    payload.revision_id = questionScope.revisionId
  }

  return payload
}

export const hasActiveComparisonFilters = (data: Pick<DisplaySettings, 'comparisons'>): boolean => {
  return (data.comparisons || []).some((item) => {
    return item?.compareBy != null || item?.comparison != null
  })
}

export const isCommentsExportAvailable = (
  settings: Pick<DisplaySettings, 'comparisons'>,
  selectedTrend: number
): boolean => {
  return selectedTrend === 1 && !hasActiveComparisonFilters(settings)
}

const normalizeComparisonOption = (value: unknown): ComparisonOptionMetadata | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const candidate = value as Partial<ComparisonOptionMetadata>
  if (!candidate.id || !candidate.title) {
    return null
  }

  return {
    id: String(candidate.id),
    title: String(candidate.title),
    abbreviation: candidate.abbreviation ?? null
  }
}

export const getFinalReportPayload = (
  payload?: Record<string, any>,
  settings?: DisplaySettings,
  scopedKeys?: Set<string>
): Record<string, any> => {
  if (payload && Object.keys(payload).length > 0) {
    return payload
  }

  if (!settings) {
    return {}
  }

  return formatResponseFilterPayload(settings) ?? formatReportPayload(settings, scopedKeys) ?? {}
}

export const isDisplaySettingsChanged = (settings: DisplaySettings, initial: DisplaySettings): boolean => {
  return !isEqual(settings.comparisons, initial.comparisons) ||
    !isEqual(settings.slices, initial.slices) ||
    !isEqual(settings.responseFilter, initial.responseFilter)
}

export const normalizeDisplaySettings = (settings?: Partial<DisplaySettings> | null): DisplaySettings => {
  const defaults = getDefaultSettings()
  const safeSettings = settings ?? undefined
  const rawComparisons = safeSettings?.comparisons
  const rawSlices = safeSettings?.slices
  const rawChartSettings = safeSettings?.chartSettings
  const comparisonSettings = Array.isArray(rawComparisons) ? rawComparisons : null

  return {
    comparisons: comparisonSettings && comparisonSettings.length > 0
      ? comparisonSettings.map((comparison) => {
        const rawUserId = (comparison as { userId?: string | null })?.userId
        return {
          compareBy: comparison?.compareBy ?? null,
          comparison: comparison?.comparison ?? null,
          comparisonOption: normalizeComparisonOption((comparison as { comparisonOption?: unknown })?.comparisonOption),
          ...(rawUserId !== undefined ? { userId: rawUserId } : {})
        }
      })
      : defaults.comparisons,
    slices: Array.isArray(rawSlices) ? rawSlices : defaults.slices,
    responseFilter: normalizeResponseFilter(safeSettings?.responseFilter),
    chartSettings: chartSettingsSwitches.reduce<Record<string, boolean>>((acc, { name }) => {
      acc[name] = Boolean(rawChartSettings?.[name] ?? defaults.chartSettings[name])
      return acc
    }, {})
  }
}

export const getPersistedDisplaySettings = (): DisplaySettings => {
  if (typeof window === 'undefined') {
    return getDefaultSettings()
  }

  try {
    const storedValue = window.localStorage.getItem(DISPLAY_SETTINGS_STORAGE_KEY)
    if (!storedValue) {
      return getDefaultSettings()
    }

    return normalizeDisplaySettings(JSON.parse(storedValue) as Partial<DisplaySettings>)
  } catch (error) {
    return getDefaultSettings()
  }
}

export const persistDisplaySettings = (settings: DisplaySettings): void => {
  if (typeof window === 'undefined') {
    return
  }

  // Persist the normalized drawer state so refreshes and drawer re-opens
  // restore the exact same comparison labels the user selected.
  window.localStorage.setItem(
    DISPLAY_SETTINGS_STORAGE_KEY,
    JSON.stringify(normalizeDisplaySettings(settings))
  )
}

export const isBaseReportDisplaySettings = (settings: DisplaySettings): boolean => {
  const hasComparison = settings.comparisons.some(
    comparison => comparison.compareBy !== null || comparison.comparison !== null
  )
  const hasSlice = settings.slices.length > 0
  return !hasComparison && !hasSlice && !hasActiveResponseFilter(settings)
}

export const getMaxLabelLength = (isFullScreen: boolean, xLabelCount: number): number => {
  if (xLabelCount > 3) return isFullScreen ? 18 : 12
  return 34
}

export const truncateLabel = (label: string, maxLen: number): string =>
  label.length > maxLen ? label.slice(0, maxLen - 3) + '...' : label

export const getTruncatedLabelMetadata = (
  label: string,
  maxLen: number
): { displayLabel: string, tooltipLabel?: string } => ({
  displayLabel: truncateLabel(label, maxLen),
  tooltipLabel: label.length > maxLen ? label : undefined
})

export const formatValue = (val: any): string => {
  if (typeof val !== 'number') return String(val)
  if (Number.isInteger(val)) return val.toString()
  return val % 1 === 0 ? val.toString() : val.toFixed(2).replace(/\.00$/, '')
}

export const hasDisplayValue = (value: unknown): boolean => {
  return value !== null && value !== undefined && (typeof value !== 'string' || value.trim() !== '')
}

const hasNumericResponseValue = (value: unknown): boolean => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 && trimmed !== '0' && trimmed !== '0%'
  }

  return false
}

export const hasMeaningfulReportCalculations = (calculations?: ICalculation[] | null): boolean => {
  return (calculations ?? []).some((calculation) =>
    (calculation.values ?? []).some(hasNumericResponseValue)
  )
}

export const surveyItemHasResponseData = (item?: KeyStatSurveyItem | null): boolean => {
  if (item == null || item.itemType === SurveyQuestionType.Title || item.itemType === SurveyQuestionType.FreeResponse) {
    return false
  }

  if (hasMeaningfulReportCalculations(item.calculations)) {
    return true
  }

  return (item.options ?? []).some((option) =>
    (option.counts ?? []).some((count) => count != null && count > 0) ||
    (option.percents ?? []).some((percent) => percent != null && percent > 0)
  )
}

export const hasMeaningfulReportResponseData = (reports?: ReportsV2 | null): boolean => {
  if (!reports) {
    return false
  }

  if (hasMeaningfulReportCalculations(reports.calculations)) {
    return true
  }

  return (reports.surveySections ?? []).some((section) => {
    const surveyItems = Array.isArray(section.surveyItems) ? section.surveyItems : [section.surveyItems]
    return surveyItems.some((item) => surveyItemHasResponseData(item))
  })
}

export const hasMeaningfulSurveyScoreSeries = (
  reports?: Pick<ReportsV2, 'surveyScores' | 'titles'> | null
): boolean => {
  const scores = reports?.surveyScores
  const titles = reports?.titles

  if (!Array.isArray(scores) || scores.length === 0 || !Array.isArray(titles) || titles.length === 0) {
    return false
  }

  return scores.some((score) => typeof score === 'number' && Number.isFinite(score) && score > 0)
}

export const hasMeaningfulReportData = (reports?: ReportsV2 | null): boolean => {
  return hasMeaningfulSurveyScoreSeries(reports) || hasMeaningfulReportResponseData(reports)
}

export const isCompletionRateDataEmpty = (completionRate?: SurveyCompletionRate | null): boolean => {
  if (!completionRate) {
    return true
  }

  const hasStudents = (completionRate.studentCount ?? 0) > 0
  const hasCompletions = (completionRate.completionCount ?? 0) > 0
  const hasScore = completionRate.surveyScore != null && completionRate.surveyScore > 0
  const hasCompletionPercent = (completionRate.completionPercent ?? 0) > 0

  return !hasStudents && !hasCompletions && !hasScore && !hasCompletionPercent
}

export const shouldShowNoResultsAvailable = (
  reports?: ReportsV2 | null,
  completionRate?: SurveyCompletionRate | null
): boolean => {
  if (!reports) {
    return false
  }

  return isCompletionRateDataEmpty(completionRate) && !hasMeaningfulReportData(reports)
}

export const shouldShowSurveyScoreChart = (selectedTrend: number, reports?: ReportsV2 | null): boolean => {
  return selectedTrend > 1 && hasMeaningfulSurveyScoreSeries(reports)
}

export const yAxisLabelFormatter = (
  value: number | string,
  isTopOrBottom2: boolean,
  questionType: any,
  report: any
): string => {
  if (isTopOrBottom2) {
    return `${value}%`
  }
  let label = String(value)
  if (questionType === 'LinearScale' || questionType === 3) {
    const idx = report.options?.findIndex((opt: any) => opt.name === value)
    const weight = idx !== undefined && idx > -1 ? report.options[idx].weights?.find((w: any) => w != null) : undefined
    label = weight !== undefined ? `${label} (${weight})` : label
  }
  if (label.length > 25) {
    label = label.slice(0, 23) + '...'
  }
  return label
}

export const getDefaultSettings = (): DisplaySettings => ({
  comparisons: [{ compareBy: null, comparison: null, comparisonOption: null }],
  slices: [],
  responseFilter: null,
  chartSettings: chartSettingsSwitches.reduce<Record<string, boolean>>((acc, { name }) => {
    acc[name] = false
    return acc
  }, {})
})

export const getReportsRequestType = (
  selectedTrend: number,
  payload: Record<string, any>
): 'history' | 'responders' | 'compare' => {
  if (selectedTrend > 1) {
    return 'history'
  }

  if (
    typeof payload?.itemId === 'string' &&
    typeof payload?.revision === 'number' &&
    (
      (Array.isArray(payload?.answers) && payload.answers.length > 0) ||
      (Array.isArray(payload?.targetAnswers) && payload.targetAnswers.length > 0)
    )
  ) {
    return 'responders'
  }

  return 'compare'
}

export const getResponseFilterQuestionOptions = (
  reports?: ReportsV2 | null
): ReportResponseFilterQuestionOption[] => {
  const sections = reports?.surveySections ?? []

  return sections.flatMap((section) => {
    const sectionTitle = section.title ?? section.name ?? 'Survey Results'
    const surveyItems = Array.isArray(section.surveyItems) ? section.surveyItems : [section.surveyItems]
    let questionNumber = 1

    return surveyItems.flatMap((item) => {
      if (!item || item.itemType === SurveyQuestionType.Title) {
        return []
      }

      const currentQuestionNumber = questionNumber
      questionNumber += 1

      if (
        item.itemType === SurveyQuestionType.FreeResponse ||
        !Array.isArray(item.options) ||
        item.options.length === 0
      ) {
        return []
      }

      const answers = item.options
        .map((option: { name?: string }) => option?.name?.trim())
        .filter((answer: string | undefined): answer is string => Boolean(answer))

      if (answers.length === 0) {
        return []
      }

      return [{
        itemId: item.itemId,
        revision: item.revision,
        prompt: item.prompt,
        sectionTitle,
        questionNumberLabel: `Q${currentQuestionNumber}`,
        answers: Array.from(new Set(answers))
      }]
    })
  })
}

export const buildVisibleKeyStats = (names: KeyStatKey[]): VisibleKeyStats =>
  ({
    average: names.includes('average'),
    median: names.includes('median'),
    mode: names.includes('mode'),
    top2: names.includes('top2'),
    bottom2: names.includes('bottom2'),
    stdev: names.includes('stdev')
  })

export const getDefaultVisibleKeyStats = (): VisibleKeyStats =>
  buildVisibleKeyStats(defaultVisibleKeyStatNames)

export const getChartTypeDefaultVisibleKeyStats = (chartType: ReportChartType): VisibleKeyStats =>
  buildVisibleKeyStats(chartTypeDefaultVisibleKeyStatNames[chartType] ?? [])

export const getEffectiveVisibleKeyStats = (
  surveyVisibleKeyStats: VisibleKeyStats,
  questionOverrides: Record<string, VisibleKeyStats> | undefined,
  questionId: string
): VisibleKeyStats => {
  return questionOverrides?.[questionId] ?? surveyVisibleKeyStats
}

export const filterVisibleCalculations = (
  calculations: ICalculation[] | null | undefined,
  visibleKeyStats: VisibleKeyStats
): ICalculation[] =>
  (calculations ?? []).filter((calculation) => {
    const key = keyStatSwitches.find(({ calculationName }) => calculation.name === calculationName)?.name
    return key ? visibleKeyStats[key] : false
  })

export const getChartLabelWidth = (labels: string[], isHorizontalLayout: boolean): number => {
  const maxLength = labels.reduce((max, label) => Math.max(max, label.length), 0)
  return isHorizontalLayout
    ? Math.min(240, Math.max(120, maxLength * 8))
    : 150
}

export const getScrollableChartWidth = (labels: string[], isHorizontalLayout: boolean): string | number => {
  if (isHorizontalLayout) {
    return '100%'
  }

  const labelWidth = getChartLabelWidth(labels, false)
  return Math.max(720, labels.length * Math.max(140, labelWidth))
}

export const getAvailableSeriesIndexes = (
  report?: KeyStatSurveyItem | null,
  expectedSeriesCount?: number
): number[] => {
  const seriesCount = expectedSeriesCount ?? Math.max(
    report?.options?.[0]?.counts?.length ?? 0,
    report?.options?.[0]?.percents?.length ?? 0,
    0
  )

  if (seriesCount === 0) {
    return []
  }

  return Array.from({ length: seriesCount }, (_, index) => index).filter((index) =>
    (report?.options ?? []).some((option) =>
      option.counts?.[index] != null || option.percents?.[index] != null
    )
  )
}

const stackedResponseSemanticColors = {
  stronglyNegative: '#8E63D2',
  negative: '#B28AE2',
  neutral: '#D9D1EA',
  positive: '#8FD1A7',
  stronglyPositive: '#32AA67'
}

const stackedResponseCategoricalPalette = [
  '#5B8CCB',
  '#F0A35E',
  '#61B7A7',
  '#B28AE2',
  '#D6C05A',
  '#E4817A'
]

const normalizeResponseLabel = (label: string): string =>
  label.trim().toLowerCase().replace(/\s+/g, ' ')

const getSemanticResponseColor = (label: string): string | null => {
  const normalized = normalizeResponseLabel(label)

  if (
    normalized.includes('strongly disagree') ||
    normalized.includes('very dissatisfied') ||
    normalized.includes('very unlikely') ||
    normalized.includes('poor')
  ) {
    return stackedResponseSemanticColors.stronglyNegative
  }

  if (
    normalized.includes('disagree') ||
    normalized.includes('dissatisfied') ||
    normalized.includes('unlikely') ||
    normalized.includes('below average')
  ) {
    return stackedResponseSemanticColors.negative
  }

  if (
    normalized.includes('neutral') ||
    normalized.includes('neither') ||
    normalized.includes('unsure') ||
    normalized.includes('average')
  ) {
    return stackedResponseSemanticColors.neutral
  }

  if (
    normalized.includes('strongly agree') ||
    normalized.includes('very satisfied') ||
    normalized.includes('very likely') ||
    normalized.includes('excellent')
  ) {
    return stackedResponseSemanticColors.stronglyPositive
  }

  if (
    normalized.includes('agree') ||
    normalized.includes('satisfied') ||
    normalized.includes('likely') ||
    normalized.includes('good')
  ) {
    return stackedResponseSemanticColors.positive
  }

  return null
}

export const getStackedResponseColors = (optionLabels: string[]): string[] => {
  let categoricalColorIndex = 0

  return optionLabels.map((label) => {
    const semanticColor = getSemanticResponseColor(label)

    if (semanticColor) {
      return semanticColor
    }

    const categoricalColor =
      stackedResponseCategoricalPalette[categoricalColorIndex % stackedResponseCategoricalPalette.length]
    categoricalColorIndex += 1
    return categoricalColor
  })
}

export const getCalculationLabel = (key: KeyStatKey): string => keyStatKeyToCalculationName[key]

export const getFilteredLinearScaleOptions = (options: any[], notApplicableValue: string): any[] =>
  options.filter(opt => opt.name !== notApplicableValue)

// CR-271: Account Owner can hit a section that's LIVE, non-rolling, and has no report row
// yet (an accepted Backend limitation — the self-heal fallback only fires once a section
// closes). The report surface (`ReportsFeatureV2`) shows this same copy inline; see
// `isAccountOwnerReportNotFound` below.
export const ACCOUNT_OWNER_REPORT_NOT_FOUND_MESSAGE =
  'No report data available yet for this section — check back once student responses start coming in.'

// Scoped to Account Owner only: no other role is expected to hit this specific known
// limitation, and a blanket bare-404 rewrite could misleadingly relabel a genuinely wrong
// section id or a deleted section for every other role.
export const isAccountOwnerReportNotFound = (error: any, isAccountOwner: boolean): boolean =>
  isAccountOwner && error?.status === HttpStatusCode.NOT_FOUND

export const handleReportNotifications = (
  reportError: any,
  completionRateError: any,
  isReportsError: boolean,
  isCompletionRateError: boolean,
  isReportsPending: boolean,
  completionRateLoading: boolean,
  isAccountOwner: boolean = false
): void => {
  const FALLBACK = 'Unknown error occurred'
  const reportStatus = reportError?.status
  const rateStatus = completionRateError?.status

  if (!reportStatus && !rateStatus && isReportsPending && completionRateLoading) return

  // CE-210: a below-threshold report renders the dedicated "Not enough responses" Results
  // Access Card in the report surface, so an error toast is redundant — and its structured
  // 403 body carries no user-facing string, so it would otherwise fall through to the
  // catch-all below and surface as a misleading "Unknown error occurred".
  if (isInsufficientResponsesError(reportError)) return

  // CR-271: the report surface's card already explains this known limitation inline
  // (see `isAccountOwnerReportNotFound`), so an error toast on top would be redundant.
  if (isAccountOwnerReportNotFound(reportError, isAccountOwner)) return

  const { BAD_REQUEST, NOT_FOUND, UNPROCESSABLE_ENTITY } = HttpStatusCode

  if (
    reportStatus === NOT_FOUND &&
    rateStatus === NOT_FOUND &&
    isReportsError &&
    isCompletionRateError
  ) {
    notification(formatReportSurfaceErrorMessage(reportError?.data ?? reportError?.message, FALLBACK), { variant: 'error' })
    return
  }

  if (
    reportStatus === BAD_REQUEST &&
    rateStatus === UNPROCESSABLE_ENTITY &&
    isReportsError &&
    isCompletionRateError
  ) {
    notification(formatReportSurfaceErrorMessage(completionRateError?.data ?? completionRateError?.message, FALLBACK), { variant: 'error' })
    return
  }

  if (
    reportStatus === BAD_REQUEST &&
    isReportsError &&
    rateStatus !== UNPROCESSABLE_ENTITY
  ) {
    notification(formatReportSurfaceErrorMessage(reportError?.data ?? reportError?.message, FALLBACK), { variant: 'info' })
    return
  }

  if (reportStatus === NOT_FOUND && isReportsError) {
    notification(formatReportSurfaceErrorMessage(reportError?.data ?? reportError?.message, FALLBACK), { variant: 'error' })
    return
  }

  if (
    reportStatus === UNPROCESSABLE_ENTITY &&
    isReportsError &&
    !isCompletionRateError
  ) {
    notification(formatReportSurfaceErrorMessage(reportError?.data ?? reportError?.message, FALLBACK), { variant: 'error' })
    return
  }

  // Catch-all for any other error status (e.g. 500, 503) not handled above
  if (isReportsError && reportStatus !== undefined) {
    notification(formatReportSurfaceErrorMessage(reportError?.data ?? reportError?.message, FALLBACK), { variant: 'error' })
  }
}

const RELEASE_DATE_DISPLAY_FORMAT = 'MMMM dd, yyyy'

const releaseDateParsePatterns = [
  'dd MMMM, yyyy',
  'd MMMM, yyyy',
  'dd MMM yyyy',
  'd MMM yyyy',
  'MMMM dd, yyyy',
  'MMMM d, yyyy',
  'MMM dd, yyyy',
  'MMM d, yyyy'
]

const trimTrailingPeriod = (value: string): string => value.trim().replace(/\.$/, '')

const parseReleaseDate = (value: string): Date | null => {
  const isoDate = parseISO(value)
  if (isValid(isoDate)) {
    return isoDate
  }

  for (const pattern of releaseDateParsePatterns) {
    const parsed = parse(value, pattern, new Date())
    if (isValid(parsed)) {
      return parsed
    }
  }

  return null
}

export const formatReportReleaseTiming = (releaseTiming: string): string => {
  const trimmedReleaseTiming = trimTrailingPeriod(releaseTiming)

  if (trimmedReleaseTiming.startsWith('after ')) {
    return `after ${formatReportReleaseTiming(trimmedReleaseTiming.replace(/^after\s+/, ''))}`
  }

  return trimmedReleaseTiming
    .split(/\s+and\s+/)
    .map((dateText) => {
      const parsedDate = parseReleaseDate(dateText)
      return parsedDate ? format(parsedDate, RELEASE_DATE_DISPLAY_FORMAT) : dateText
    })
    .join(' and ')
}

const buildScheduledReleaseMessage = (releaseTiming: string): string => {
  const formattedReleaseTiming = formatReportReleaseTiming(releaseTiming)
  const releasePreposition = formattedReleaseTiming.startsWith('after ') ? '' : 'on '

  return `Results are currently scheduled to release ${releasePreposition}${formattedReleaseTiming}.`
}

// Anything after the release-date sentence (e.g. Backend's CR-271 Account-Owner
// addendum, "Course section reports are available now.") is preserved verbatim
// instead of being swallowed into the date capture group below — a greedy
// `(.+)\.?$` capture used to pull trailing sentences into "the date," which then
// failed to parse and silently lost the friendly date formatting.
const appendTrailingSentence = (formattedMessage: string, trailingText: string): string => {
  const trimmedTrailing = trailingText.trim()
  return trimmedTrailing ? `${formattedMessage} ${trimmedTrailing}` : formattedMessage
}

const formatScheduledReleaseMessage = (message: string): string => {
  const trimmedMessage = message.trim()
  const availableOnMatch = trimmedMessage.match(/^Results for this survey will be available on ([^.]+)\.?(.*)$/)
  const availableAfterMatch = trimmedMessage.match(/^Results for this survey will be available after ([^.]+)\.?(.*)$/)

  if (availableOnMatch?.[1]) {
    return appendTrailingSentence(
      buildScheduledReleaseMessage(trimTrailingPeriod(availableOnMatch[1])),
      availableOnMatch[2]
    )
  }

  if (availableAfterMatch?.[1]) {
    return appendTrailingSentence(
      buildScheduledReleaseMessage(`after ${trimTrailingPeriod(availableAfterMatch[1])}`),
      availableAfterMatch[2]
    )
  }

  return message
}

export const formatReportSurfaceErrorMessage = (
  rawMessage: unknown,
  fallback = 'Unknown error occurred'
): string => {
  if (typeof rawMessage === 'string' && rawMessage.trim().length > 0) {
    if (isInstructorGradePostingReleaseMessage(rawMessage)) {
      return rawMessage
    }
    return formatScheduledReleaseMessage(rawMessage)
  }

  if (!rawMessage || typeof rawMessage !== 'object') {
    return fallback
  }

  const message = rawMessage as Record<string, unknown>
  const directMessage = [message.error, message.message]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0)

  if (directMessage) {
    if (isInstructorGradePostingReleaseMessage(directMessage)) {
      return directMessage
    }
    return formatScheduledReleaseMessage(directMessage)
  }

  const releaseDates = [
    message.earlyAccess_1ReleaseDate,
    message.earlyAccess_2ReleaseDate,
    message.releaseDate
  ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)

  if (releaseDates.length === 1) {
    return buildScheduledReleaseMessage(releaseDates[0])
  }

  if (releaseDates.length > 1) {
    return buildScheduledReleaseMessage(`after ${releaseDates.join(' and ')}`)
  }

  return fallback
}

export const getFlaggedCommentErrorMessage = (error: any): string => {
  if (error?.message && typeof error.message === 'object' && !Array.isArray(error.message)) {
    const firstKey = Object.keys(error.message)[0]
    const firstMsg = error.message[firstKey]?.[0]
    return `Failed to update flagged comments: ${firstMsg || ''}`
  }
  if (typeof error?.message === 'string') {
    return `Failed to update flagged comments: ${error.message}`
  }
  return 'Failed to update flagged comments'
}

// Must stay byte-identical to NO_REPORT_ACCESS_MESSAGE in Backend's
// backend/utils/report_permission_utils.py — this is an exact string match, not a status-code
// check, so a backend wording/punctuation change silently drops the Results Access Card back
// to the generic error card. No shared constant is possible across the Python/TS boundary;
// if this ever changes on the backend, update it here too.
export const NO_REPORT_ACCESS_MESSAGE = 'Your role does not have access to reports for this survey project.'

export const isNoReportAccessError = (error: any): boolean =>
  error?.status === HttpStatusCode.FORBIDDEN && error?.data?.error === NO_REPORT_ACCESS_MESSAGE

// CE-210 — a section report below the survey project's Results Threshold (behavior `hide`).
// Kept distinct from isNoReportAccessError: that one matches a *string* at error.data.error;
// this one matches an *object* at error.data.error carrying { code, current, min }.
export const isInsufficientResponsesError = (error: any): boolean =>
  error?.status === HttpStatusCode.FORBIDDEN &&
  error?.data?.error?.code === 'insufficient_responses'

/** Exact string body used by some export endpoints when below the Results Threshold. */
export const NOT_ENOUGH_RESPONSES_EXPORT_MESSAGE = 'There are not enough responses to export.'

const INSUFFICIENT_RESPONSES_EXPORT_FALLBACK =
  'There are not enough responses to export individual responses for this report.'

/**
 * Read a user-facing message from a report-export failure.
 * Aggregate CSV/PDF use `responseType: 'blob'`, so a JSON 403 body arrives as a Blob and the
 * api interceptor falls back to a generic message — parse the Blob (or string/object body)
 * here so the export catch can toast the real copy.
 */
const readBlobLikeText = async (value: unknown): Promise<string | null> => {
  if (value == null || typeof value !== 'object') return null
  const blob = value as { text?: () => Promise<string>, arrayBuffer?: () => Promise<ArrayBuffer> }
  try {
    if (typeof blob.text === 'function') {
      return (await blob.text()).trim()
    }
    if (typeof blob.arrayBuffer === 'function') {
      return new TextDecoder().decode(await blob.arrayBuffer()).trim()
    }
  } catch {
    return null
  }
  return null
}

export const resolveExportErrorMessage = async (error: unknown): Promise<string | null> => {
  const err = error as { status?: number, data?: unknown, message?: unknown } | null
  if (err == null || typeof err !== 'object') return null

  let body: unknown = err.data
  // Export managers often use responseType: 'blob', so JSON 403 bodies arrive as Blobs
  // (Jest also fails `instanceof Blob` checks — duck-type via text/arrayBuffer).
  const blobText = await readBlobLikeText(body)
  if (blobText != null && blobText !== '') {
    try {
      body = JSON.parse(blobText)
    } catch {
      body = null
    }
  }

  const payloadError = (body as { error?: unknown } | null)?.error
  if (typeof payloadError === 'string' && payloadError.trim() !== '') {
    return payloadError.trim()
  }
  if (
    payloadError != null &&
    typeof payloadError === 'object' &&
    (payloadError as { code?: unknown }).code === 'insufficient_responses'
  ) {
    return INSUFFICIENT_RESPONSES_EXPORT_FALLBACK
  }

  // Interceptor may have already extracted a useful string for JSON responseType exports.
  if (
    typeof err.message === 'string' &&
    err.message.trim() !== '' &&
    err.message !== 'An unexpected error occurred. Please try again later.'
  ) {
    return err.message.trim()
  }

  return null
}

export const buildSectionExpansionMap = (sectionCount: number, expanded: boolean): Record<number, boolean> => {
  return Array.from({ length: sectionCount }).reduce<Record<number, boolean>>((acc, _, idx) => {
    acc[idx] = expanded
    return acc
  }, {})
}

export const areAllSectionsExpanded = (expandedSections: Record<number, boolean>, sectionCount: number): boolean => {
  if (sectionCount === 0) {
    return false
  }
  return Array.from({ length: sectionCount }).every((_, idx) => !!expandedSections[idx])
}
